import React, { useEffect, useState } from "react";
import { 
  Button,
  TextField,
  FormHelperText,
  Box, Grid,
  CircularProgress,
  Typography,
} from "@mui/material";
import { styled } from '@mui/system';
import Navbar from "../components/NavBar/Navbar";
import Footer from "../components/Shared/Footer";
import { useTheme } from '@mui/material/styles';
import { useOrderContext } from "../hooks/useContext/useOrderContext";
import SimpleAlert from '../components/Shared/Alert';
import { useAvailableItemConfigContext } from "../hooks/useContext/useAvailableItemConfigContext";
import ErrorBox from "../components/Shared/ErrorBox";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import useFetchOpenStatus from "../hooks/useFetchOpenStatus";
import validator from 'validator';
import CheckoutForm from "../components/Checkout_Page/StripeCheckoutForm";
import CartComponent from "../components/Checkout_Page/CartComponent";
import { useTranslation } from "../hooks/useTranslation";
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';

const GridContainerMain = styled(Grid)(() => ({
  minHeight: "100vh",
  padding: '50px 10px 0',
  paddingTop: '50px',
  paddingBottom: '50px',
  display: 'flex',
  justifyContent: 'center',
}));

// const Center = styled('div')({
//     display: "flex",
//     paddingTop: "105px",
//     paddingBottom: "105px",
//     justifyContent: "center",
// });


const Checkout = () => {
  const theme = useTheme();
  const { t, language } = useTranslation();
  const stripePromise = loadStripe(`${process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY}`, {
    locale: language === 'pl' ? 'pl' : 'en',
  });
  const [buttonText, setButtonText] = useState(t('submitOrder'));
  const [isDisabled, setIsDisabled] = useState(false);
  const { order, paymentMethod, dispatch } = useOrderContext();
  const { openStatus } = useAvailableItemConfigContext();
  const [emptyFields, setEmptyFields] = useState([]);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [hasAlcohol, setHasAlcohol] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    paymentMethod: 'online',
    // paymentMethod: paymentMethod,
  });

  const handleCustomerInfoChange = (field, value) => {
    setCustomerInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  useFetchOpenStatus();

  useEffect(() => {
    if (!order || order.length === 0) {
      setHasAlcohol(false);
      return;
    }

    const containsAlcohol = order.some((item) =>
      item.itemSubCategory === 'Alcoholic' || item.itemSubCategory === 'Shots'
    );

    setHasAlcohol(containsAlcohol);
  }, [order]);

  //stripe elements appearance
  const loader = 'auto';
  const appearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: theme.palette.primary.main,
      colorBackground: theme.palette.component.componentBackground,
      colorText: theme.palette.common.grey,
      colorDanger: theme.palette.common.error,
      fontFamily: theme.typography.fontFamily,
      spacingUnit: '4px',
      borderRadius: '1px',
    }
  };

  //fetch strip payment intent
  useEffect(() => {
    if (order.length > 0 && paymentMethod === 'online' && openStatus === true && !clientSecret) {
    fetch(process.env.REACT_APP_SERVER_URL + "/api/orders/createPaymentIntent", {
      method: "POST",
      headers: {"Content-Type": "application/json"},
      body: JSON.stringify(order),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.clientSecret) {
          setClientSecret(data.clientSecret)
        }
      })
      .catch((error) => {
        console.error("Error creating payment intent:", error);
      });
    }
  }, [order, paymentMethod, openStatus, clientSecret]);


  //no payment/stripe submission
  const noPaymentOrder = async (customerInfo, orderItems) => {
    setIsDisabled(true);
    setButtonText("Loading...")

    let emptyFieldsList = [];

    if (!customerInfo.firstName) {
      emptyFieldsList.push("firstName");
      setError("lease enter first name");
    }
    if (!customerInfo.lastName) {
      emptyFieldsList.push("lastName");
      setError("Please enter last name");
    }
    if (!customerInfo.phoneNumber) {
      emptyFieldsList.push("phoneNumber");
      setError("Please enter phone number");
    } else {
      if (!matchIsValidTel(customerInfo.phoneNumber)) {
        emptyFieldsList.push("phoneNumber");
        setError("Please enter a valid phone number");
      }
    }
    // if (!customerInfo.email) {
    //   emptyFieldsList.push("email");
    //   setError("Please enter email");
    // }
    // if (customerInfo.email && !validator.isEmail(customerInfo.email)) {
    //   emptyFieldsList.push("email");
    //   setError("Invalid email address");
    // }
    if (emptyFieldsList.length > 0) {
      setEmptyFields(emptyFieldsList);
      if (emptyFieldsList.length > 1) {
        setError("Please fill in all fields");
        }
      setIsDisabled(false);
      setButtonText("Submit Order")
      return;
    }
    else {
      setEmptyFields([]);
      setError(null);
    }
  
    const orderData = {
      name: customerInfo.firstName + ' ' + customerInfo.lastName,
      phoneNumber: customerInfo.phoneNumber,
      email: customerInfo.email || '',
      orderItems: orderItems.map(item => ({
        itemName: item.itemName,
        itemQuantity: item.itemQuantity,
        itemPrice: item.itemPrice,
        itemSize: item.itemSize,
        itemType: item.itemType,
        specialInstructions: item.specialInstructions || '',
      })),
      totalPrice: orderItems.reduce((acc, item) => acc + (item.itemQuantity * item.itemPrice), 0),
      paymentMethod: paymentMethod === 'inPerson',
    };

    dispatch({type: 'SET_USER_INFO', payload: {name: orderData.name, email: orderData.email || ''}});

    try {
      const response = await fetch(process.env.REACT_APP_SERVER_URL + "/api/orders/newOrder", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(orderData),
      });

      const data = await response.json();
      if (response.ok) {
        setEmptyFields([]);
        setError(null);
        if (data?._id) {
          dispatch({ type: 'SET_LAST_ORDER_ID', payload: data._id });
        }
        setIsDisabled(false);
        setButtonText("Submit Order")
        window.location.href = "/complete";
      } else {
        setIsDisabled(false);
        setButtonText("Submit Order")
        if (data.error === t('storeClosed')) {
          setIsDisabled(true);
        }
        setError(data.error);
      }
    } catch (error) {
      setIsDisabled(false);
      setButtonText("Submit Order")
      setError(error);
      console.log('2');
    };
  };

  //scroll to top is cart empty
  useEffect(() => {
    if (order.length === 0) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [order.length]);

  return (
    <div style={{ backgroundColor: theme.palette.common.background }}>
      <Navbar />
      {openStatus === false && <SimpleAlert text={t('storeClosed')} showAlert={!openStatus} type={'error'} />}
      <GridContainerMain container spacing={0}>
        {order.length > 0 ? (
        <>
          <Grid container spacing={2} justifyContent="center">

            <Grid item xs={12} sm={12} md={8} lg={4.5} xl={3.5}>

              {/*"Checkout" Box */}
              <Box component="form" autoComplete="on" sx={{ padding: 2, border: '1px solid #ccc', marginBottom: 2, backgroundColor: theme.palette.component.componentBackground }}>
                <Typography variant="body2" component="p" sx={{ textAlign: 'left' }} >
                  <b>{t('checkout')}</b>
                </Typography>
              </Box>

              {hasAlcohol && (
                <Box
                  sx={{
                    width: "100%",
                    padding: "12px 18px",
                    backgroundColor: "#fff3cd",
                    border: "1px solid #ffeeba",
                    borderRadius: "4px",
                    marginBottom: 3,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {t('alcoholWarning')}
                  </Typography>
                </Box>
              )}

              {/*Order Mobile Box */}
              <Box  display={{ xs: "block", sm: "block", md:"none", lg:"none", xl:"none" }} sx={{paddingBottom: 2}}>
                <CartComponent cartItem={{order, customerInfo}}/>
              </Box>

              {/*Customer Info Box */}
              <Box sx={{ padding: 2, border: '1px solid #ccc', marginBottom: 2, backgroundColor: theme.palette.component.componentBackground }}>
                <Typography variant="body2" component="p" sx={{ textAlign: 'left', padding: "15px 0px" }} >
                  <b>{t('customerInformation')}</b>
                </Typography>
                <TextField
                  label={t('firstName')}
                  variant="outlined"
                  id="given-name"
                  name="given-name"
                  autoComplete="given-name"
                  fullWidth
                  required
                  autoFocus
                  error={emptyFields.includes("firstName")}
                  value={customerInfo.firstName}
                  onChange={(e) => handleCustomerInfoChange('firstName', e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  label={t('lastName')}
                  variant="outlined"
                  id="family-name"
                  name="family-name"
                  autoComplete="family-name"
                  fullWidth
                  required
                  value={customerInfo.lastName}
                  error={emptyFields.includes("lastName")}
                  onChange={(e) => handleCustomerInfoChange('lastName', e.target.value)}
                  sx={{ mb: 2 }}
                />
                <MuiTelInput
                  label={t('phoneNumber')}
                  id="tel"
                  name="tel"
                  autoComplete="tel"
                  fullWidth
                  required
                  defaultCountry="PL"
                  value={customerInfo.phoneNumber}
                  error={emptyFields.includes("phoneNumber")}
                  onChange={(value) => handleCustomerInfoChange('phoneNumber', value || '')}
                  sx={{ mb: 2 }}
                />
                <FormHelperText sx={{color: theme.palette.common.offGrey, mb: 2, ml: 1}}>{t('textWhenReady')}</FormHelperText>
                <TextField
                  variant="outlined"
                  label={t('email')}
                  id="email"
                  name="email"
                  autoComplete="username"
                  fullWidth
                  // required
                  value={customerInfo.email}
                  // error={emptyFields.includes("email")}
                  onChange={(e) => handleCustomerInfoChange('email', e.target.value)}
                  // sx={{ mb: 2 }}
                />
                <FormHelperText sx={{color: theme.palette.common.offGrey, mb: 2, ml: 1}}>{t('emailCopyOfOrder')}</FormHelperText>
                {error && <ErrorBox error={error} color={"error"} />}
                {!openStatus && paymentMethod === 'inPerson' && <ErrorBox error={t('storeClosed')} color={"error"} />}
              </Box>

              {/*Stripe Payment Box */}
              {paymentMethod !== 'inPerson' && (
                <Box sx={{ padding: 2, border: '1px solid #ccc', marginBottom: 2, backgroundColor: theme.palette.component.componentBackground }} >
                  <Typography variant="body2" component="p" sx={{ textAlign: 'left', padding: "15px 0px" }} >
                    <b>{t('payment')}</b>
                  </Typography>

                  <Box sx={{minHeight: '400px'}}>
                    {!clientSecret && paymentMethod === 'online' && (
                      <Box sx={{ minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <CircularProgress/>
                      </Box>
                    )}
                    {clientSecret && paymentMethod === 'online' && (
                      <Elements key={clientSecret} options={{clientSecret, appearance, loader}} stripe={stripePromise}>
                        <CheckoutForm 
                          order={order}
                          customerInfo={customerInfo}
                          paymentMethod={paymentMethod}
                          setEmptyFields={setEmptyFields}
                          setError={setError}
                          clientSecret={clientSecret}
                        />
                      </Elements>
                    )}
                  </Box>

                </Box>
              )}
              {/*No Payment Box with only submit button */}
              {paymentMethod === 'inPerson' && (
                <Box sx={{ padding: 2, border: '1px solid #ccc', marginBottom: 2, backgroundColor: theme.palette.component.componentBackground }} >
                  {isDisabled && 
                    <Box sx={{ padding: '10px 0', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      <CircularProgress/>
                    </Box>
                  }
                  <Button variant="primaryButton" disabled={isDisabled || !openStatus} sx={{ borderRadius: "10px", width: '100%' }} onClick={() => noPaymentOrder(customerInfo, order)}>
                    {buttonText}
                  </Button>
                </Box>
              )}

            </Grid>

            {/* Right Column: Cart Box */}
            <Grid item xs={12} sm={12} md={4} lg={3.5} xl={2.5}>
              <Box display={{ xs: "none", sm: "none", md:"block", lg:"block", xl:"block" }} sx={{position: 'sticky', top: '80px' }}>
                <CartComponent cartItem={{order, customerInfo}}/>
              </Box>
            </Grid>
          </Grid>
        </>
        ) : (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            padding: 2,
            border: `1px solid ${theme.palette.component.borderColor}`,
            marginBottom: 2,
            backgroundColor: theme.palette.component.componentBackground,
            width: '80%',
            maxWidth: '600px',
            height: '270px',
            borderRadius: '10px',
            textAlign: 'center',
            boxSizing: 'border-box',
            elevation: 1,
          }}>
            <Typography variant="h4" align='center' sx={{ textAlign: 'center', padding: "20px 0 50px 0" }}>
              Your cart is empty!
            </Typography>
            <Button href="/order" variant="primaryButton" align='center'>
              View Menu
            </Button>
          </Box>
        )}
      </GridContainerMain>
      <Footer />
    </div>
  );
};

export default Checkout;